import { useAppSelector } from "Components/Hooks/hooks";
import React, { useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import { VerticalMarket } from "types/config.api";
import { ErrorToast } from "utils/toast";

export interface UserFilterValues {
  status: ("active" | "disable")[];
  subscription: ("purchased" | "not_purchased")[];
  vertical: (number | string)[];
}

interface Props {
  show: boolean;
  onCloseClick: () => void;
  onFilterChange: (filters: UserFilterValues) => void;
}

const STATUS_LIST = ["Active", "Disable"];
const SUBSCRIPTION_LIST = ["Purchased", "Not Purchased"];

const FilterUser: React.FC<Props> = ({
  show,
  onCloseClick,
  onFilterChange,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<string[]>(
    []
  );
  const [selectedVertical, setSelectedVertical] = useState<(number | string)[]>(
    []
  );
  const { configData } = useAppSelector((state) => state.config);

  const handleStatusChange = (status: string) => {
    setSelectedStatus((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleSubscriptionChange = (subscription: string) => {
    setSelectedSubscription((prev) =>
      prev.includes(subscription)
        ? prev.filter((s) => s !== subscription)
        : [...prev, subscription]
    );
  };

  const handleVerticalChange = (vId: number | string) => {
    const verticalId = vId;
    setSelectedVertical((prev) =>
      prev.includes(verticalId)
        ? prev.filter((v) => v !== verticalId)
        : [...prev, verticalId]
    );
  };

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      selectedStatus.length === 0 &&
      selectedSubscription.length === 0 &&
      selectedVertical.length === 0
    ) {
      ErrorToast("Please select at least one filter option.");
      return;
    }
    console.log(selectedVertical);

    // Convert to API format
    const filters: UserFilterValues = {
      status: selectedStatus.map(
        (s) => s.toLowerCase().replace(" ", "_") as "active" | "disable"
      ),
      subscription: selectedSubscription.map(
        (s) =>
          s.toLowerCase().replace(" ", "_") as "purchased" | "not_purchased"
      ),
      vertical: selectedVertical,
    };

    onFilterChange?.(filters);
    onCloseClick();
  };

  const handleClear = () => {
    setSelectedStatus([]);
    setSelectedSubscription([]);
    setSelectedVertical([]);
    onFilterChange?.({ status: [], subscription: [], vertical: [] });
    onCloseClick();
  };

  return (
    <Offcanvas direction="end" isOpen={show} toggle={onCloseClick}>
      <OffcanvasHeader className="bg-greyF5" toggle={onCloseClick}>
        <div className="text-black2D font-primary fw-medium fs-16">Filters</div>
      </OffcanvasHeader>

      <form className="d-flex flex-column justify-content-end h-100">
        <OffcanvasBody className="pt-3">
          {/* STATUS */}
          <Label className="text-grey89 font-primary fw-medium fs-14 mb-1 mt-2 mb-2">
            STATUS
          </Label>
          <Row className="g-2 mb-1">
            {STATUS_LIST.map((status, index) => (
              <Col key={index}>
                <Input
                  className="form-check-input me-2"
                  type="checkbox"
                  checked={selectedStatus.includes(status)}
                  onChange={() => handleStatusChange(status)}
                  id={`status-${index}`}
                  style={{ width: 20, height: 20 }}
                />
                <Label
                  className="text-black2D font-primary fw-normal fs-16 text-capitalize"
                  htmlFor={`status-${index}`}
                >
                  {status}
                </Label>
              </Col>
            ))}
          </Row>

          {/* Subscription */}
          <Label className="text-grey89 font-primary fw-medium fs-14 mb-1 mt-3 mb-2">
            SUBSCRIPTION
          </Label>
          <Row className="g-2 mb-1">
            {SUBSCRIPTION_LIST.map((subscription, index) => (
              <Col key={index}>
                <Input
                  className="form-check-input me-2"
                  type="checkbox"
                  checked={selectedSubscription.includes(subscription)}
                  onChange={() => handleSubscriptionChange(subscription)}
                  id={`subscription-${index}`}
                  style={{ width: 20, height: 20 }}
                />
                <Label
                  className="text-black2D font-primary fw-normal fs-16 text-capitalize"
                  htmlFor={`subscription-${index}`}
                >
                  {subscription}
                </Label>
              </Col>
            ))}
          </Row>

          {/* Verticals */}
          <Label className="text-grey89 font-primary fw-medium fs-14 mb-1 mt-3 mb-2">
            VERTICALS
          </Label>
          <Row className="g-2 mb-1">
            {configData?.vertical_markets?.map((vertical: VerticalMarket) => (
              <Col xs={6} key={vertical.id}>
                <Input
                  className="form-check-input me-2"
                  type="checkbox"
                  checked={selectedVertical.includes(
                    vertical.id === "own" ? "own" : Number(vertical.id)
                  )}
                  onChange={() =>
                    handleVerticalChange(
                      vertical.id === "own" ? "own" : Number(vertical.id)
                    )
                  }
                  id={`vertical-${vertical.id}`}
                  style={{ width: 20, height: 20 }}
                />
                <Label
                  className="text-black2D font-primary fw-normal fs-16 text-capitalize"
                  htmlFor={`vertical-${vertical.id}`}
                >
                  {vertical.title}
                </Label>
              </Col>
            ))}
          </Row>
        </OffcanvasBody>

        {/* FOOTER */}
        <div className="offcanvas-footer border-top p-3 text-center hstack gap-2">
          <button
            type="button"
            className="btn bg-greyF5 text-black2D fs-14 fw-normal font-primary w-100 br-7"
            onClick={handleClear}
          >
            Clear
          </button>

          <button
            type="submit"
            className="btn bg-primary text-white fs-14 fw-normal font-primary w-100 br-7"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </form>
    </Offcanvas>
  );
};

export default FilterUser;
